import React, { useEffect, useRef, useState, useCallback } from 'react';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { Button, ButtonText, ButtonSpinner } from '@/components/gluestack/button';
import { Pressable } from '@/components/gluestack/pressable';
import { ScrollView, Platform } from 'react-native';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import * as Location from 'expo-location';
import { Search02Icon } from '@hugeicons-pro/core-solid-rounded';
import { CloseIcon } from '@/components/gluestack/icon';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { PlaceSearchResult } from '@/components/providers/LocationProvider';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import ExternalApiService, { IGeoPlaceSuggestion } from '@/services/ExternalApiService';

export interface ILocationPlacePickerProps extends IContextSheetContentProps {
  onSelect: (place: PlaceSearchResult) => void | Promise<void>;
  autoFocus?: boolean;
}

// Place suggestions have no coordinates until picked; device geocoding results already do
type IPlaceOption = PlaceSearchResult | IGeoPlaceSuggestion;

const isResolvedPlace = (option: IPlaceOption): option is PlaceSearchResult => 'latitude' in option;

export default function LocationPlacePicker({
  onClose,
  payload,
  onSelect: directOnSelect,
  autoFocus: directAutoFocus = true,
}: ILocationPlacePickerProps & { payload?: Partial<ILocationPlacePickerProps> }) {
  // Extract props from payload (ContextSheet pattern) or use direct props
  const onSelect = payload?.onSelect ?? directOnSelect;
  const autoFocus = payload?.autoFocus ?? directAutoFocus;
  const { t } = useTranslation();
  const { showErrorToast } = useToastMessages();
  const isLargeScreen = useIsLargeScreen();

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<IPlaceOption[]>([]);
  const [resolvingKey, setResolvingKey] = useState<string | null>(null);

  const searchRef = useDelayedAutoFocus(autoFocus);

  const buildDisplayName = useCallback((parts: Array<string | undefined | null>): string => {
    const v = parts.filter(Boolean).map((x) => String(x));
    return v.join(', ');
  }, []);

  const searchPlaces = useCallback(
    async (rawQuery: string, signal?: AbortSignal): Promise<IPlaceOption[]> => {
      const q = rawQuery.trim();
      if (!q) return [];

      try {
        const suggestions = await ExternalApiService.suggestGeoPlaces(q, signal);
        if (suggestions.length > 0) return suggestions;
      } catch (e) {
        if (signal?.aborted) throw e;
        showErrorToast(t('settings.location.searchError', 'Place search failed'), e);
      }

      try {
        const geocoded = await Location.geocodeAsync(q);
        const results: PlaceSearchResult[] = [];
        for (const g of geocoded) {
          const lat = (g as any).latitude as number;
          const lon = (g as any).longitude as number;
          let city = (g as any).city as string | undefined;
          let region = (g as any).region as string | undefined;
          let country = (g as any).country as string | undefined;
          const name = (g as any).name as string | undefined;
          const street = (g as any).street as string | undefined;

          if (!region || !country || !city) {
            try {
              const rev = await Location.reverseGeocodeAsync({ latitude: lat, longitude: lon });
              const addr = rev?.[0] as any;
              city = city || addr?.city || addr?.subregion || addr?.district || addr?.name;
              region = region || addr?.region;
              country = country || addr?.country;
            } catch {}
          }

          const displayName = buildDisplayName([city || name || street, region, country]) || q;
          results.push({ displayName, latitude: lat, longitude: lon } as PlaceSearchResult);
        }
        return results;
      } catch {
        return [];
      }
    },
    [buildDisplayName]
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const run = async () => {
      const q = query.trim();
      if (q.length < 2) {
        setResults([]);
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        const r = await searchPlaces(q, controller.signal);
        if (!controller.signal.aborted) {
          setResults(r);
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    const handle = setTimeout(run, 400);
    return () => {
      clearTimeout(handle);
      abortControllerRef.current?.abort();
    };
  }, [query, searchPlaces]);

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    searchRef.current?.focus?.();
  };

  const selectingRef = useRef(false);
  const resolveControllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => resolveControllerRef.current?.abort(), []);

  const handleSelect = async (option: IPlaceOption, key: string) => {
    if (selectingRef.current) return;
    selectingRef.current = true;

    try {
      let place: PlaceSearchResult;
      if (isResolvedPlace(option)) {
        place = option;
      } else {
        const controller = new AbortController();
        resolveControllerRef.current = controller;
        setResolvingKey(key);
        try {
          const coordinates = await ExternalApiService.resolveGeoPlace(option, controller.signal);
          place = {
            displayName: option.displayName,
            countryCode: option.countryCode,
            ...coordinates,
          };
        } catch (e) {
          if (!controller.signal.aborted) {
            showErrorToast(t('settings.location.searchError', 'Place search failed'), e);
          }
          return;
        } finally {
          setResolvingKey(null);
        }
        // The picker was closed while the coordinates were loading
        if (controller.signal.aborted) return;
      }

      await onSelect(place);
      onClose?.();
    } finally {
      selectingRef.current = false;
    }
  };

  return (
    <VStack className={`gap-4 pt-1 ${isLargeScreen ? 'flex-1' : ''}`}>
      <Input variant="outline">
        <InputSlot className="pl-3">
          <HugeIconsWrapper icon={Search02Icon} size={16} className="color-typography-700" />
        </InputSlot>
        <InputField
          ref={searchRef}
          placeholder={t('settings.location.searchPlaceholder', 'City, postal code or address')}
          value={query}
          onChangeText={setQuery}
          autoCorrect={false}
          autoCapitalize="none"
          returnKeyType="search"
          inputMode="search"
          autoComplete="off"
          textContentType="none"
          importantForAutofill="no"
        />
        {query?.length ? (
          <InputSlot onPress={clearSearch} className="pr-3">
            <InputIcon as={CloseIcon} />
          </InputSlot>
        ) : null}
      </Input>

      {loading ? (
        <HStack className={`items-center justify-center py-6 ${isLargeScreen ? 'flex-1' : ''}`}>
          <ButtonSpinner />
          <Text className="ml-2 text-typography-500">{t('common.loading', 'Loading...')}</Text>
        </HStack>
      ) : results.length === 0 ? (
        <Text className={`text-typography-500 ${isLargeScreen ? 'flex-1' : ''}`}>
          {query.trim().length < 2
            ? t('settings.location.searchHint', 'Type at least 2 characters')
            : t(
                'settings.location.noResults',
                'No matching places. Check the spelling or try a nearby city.'
              )}
        </Text>
      ) : (
        <ScrollView className={isLargeScreen ? 'flex-1' : ''} keyboardShouldPersistTaps="handled">
          <VStack className="divide-y divide-background-100 web:divide-y-0">
            {results.map((r) => {
              const key = `${isResolvedPlace(r) ? `${r.latitude},${r.longitude}` : r.type}#${r.displayName}`;
              return (
                <Pressable
                  key={key}
                  onPress={() => handleSelect(r, key)}
                  disabled={!!resolvingKey}
                  className="rounded-xl px-3 py-3 active:bg-background-50 web:cursor-pointer web:hover:bg-background-200/50">
                  <HStack className="items-center justify-between gap-2">
                    <Text className="flex-1">{r.displayName}</Text>
                    {resolvingKey === key ? <ButtonSpinner /> : null}
                  </HStack>
                </Pressable>
              );
            })}
          </VStack>
        </ScrollView>
      )}

      {isLargeScreen ? (
        <HStack className="shrink-0 justify-end pb-2">
          <Button onPress={() => onClose?.()} action="neutral">
            <ButtonText>{t('common.cancel', 'Cancel')}</ButtonText>
          </Button>
        </HStack>
      ) : null}
    </VStack>
  );
}

LocationPlacePicker.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'md',
  height: 'xl',
  scrollableContent: true,
} satisfies ISheetConfig;
